import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import SecurityGate from "@/pages/SecurityGate";
const Activer = lazy(() => import("@/pages/Activer"));
const Home = lazy(() => import("@/pages/Home"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const Cases = lazy(() => import("@/pages/Cases"));
const Etl = lazy(() => import("@/pages/Etl"));
const FlowMonitor = lazy(() => import("@/pages/FlowMonitor"));
const Matching = lazy(() => import("@/pages/Matching"));
const Indicators = lazy(() => import("@/pages/Indicators"));
const Qc = lazy(() => import("@/pages/Qc"));
const Saisie = lazy(() => import("@/pages/Saisie"));
const Dhis2 = lazy(() => import("@/pages/Dhis2"));
const Admin = lazy(() => import("@/pages/Admin"));
const Settings = lazy(() => import("@/pages/Settings"));
const Reports = lazy(() => import("@/pages/Reports"));
const Strides = lazy(() => import("@/pages/Strides"));
const Ghs = lazy(() => import("@/pages/Ghs"));
const Crisis = lazy(() => import("@/pages/Crisis"));
const Compare = lazy(() => import("@/pages/Compare"));
const Coordination = lazy(() => import("@/pages/Coordination"));
const Governance = lazy(() => import("@/pages/Governance"));
const Verify = lazy(() => import("@/pages/Verify"));
const KoboForms = lazy(() => import("@/pages/KoboForms"));
const QualityGate = lazy(() => import("@/pages/QualityGate"));
const Notebooks = lazy(() => import("@/pages/Notebooks"));
const KoboGuide = lazy(() => import("@/pages/KoboGuide"));
const AccessCodes = lazy(() => import("@/pages/AccessCodes"));
const AccessMatrix = lazy(() => import("@/pages/AccessMatrix"));
const MetadataRegistry = lazy(() => import("@/pages/MetadataRegistry"));
const DataIssues = lazy(() => import("@/pages/DataIssues"));
const Decisions = lazy(() => import("@/pages/Decisions"));
const KoboMapping = lazy(() => import("@/pages/KoboMapping"));
const Sources = lazy(() => import("@/pages/Sources"));
const DataUse = lazy(() => import("@/pages/DataUse"));
import { SyntheticBanner } from "@/components/SyntheticBanner";
import { LEGACY_ROUTES } from "@/lib/nav";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500 text-sm">Chargement…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.security_gate) return <SecurityGate gate={user.security_gate} />;
  return children;
}

// Garde d'espace : 403 côté API, mais on évite d'afficher une page hors périmètre.
function Space({ espace, children }) {
  const { user } = useAuth();
  const ok = (user.acces?.espaces || []).includes(espace);
  return ok ? children : <Navigate to="/app" replace />;
}

function LegacyRedirect() {
  const { pathname, search } = useLocation();
  const target = LEGACY_ROUTES[pathname] || "/app";
  return <Navigate to={target + search} replace />;
}

const PAGES = {
  strides: { "tableau-de-bord": <Strides />, cas: <Cases />, indicateurs: <Indicators /> },
  ghs: { "tableau-de-bord": <Ghs />, indicateurs: <Indicators /> },
  crisis: { "tableau-de-bord": <Crisis />, indicateurs: <Indicators /> },
  reponse: { "vue-d-ensemble": <Dashboard />, carte: <MapPage />, performance: <Compare />, coordination: <Coordination />, indicateurs: <Indicators />, decisions: <Decisions /> },
  qualite: { moniteur: <FlowMonitor />, violations: <Qc />, corrections: <QualityGate />, appariement: <Matching />, etl: <Etl />, saisie: <Saisie />, problemes: <DataIssues /> },
  analyses: { notebooks: <Notebooks />, decisions: <Decisions />, "utilisation-donnees": <DataUse /> },
  rapports: { livrables: <Reports />, dhis2: <Dhis2 /> },
  gouvernance: { referentiels: <Governance />, matrice: <AccessMatrix />, dictionnaire: <MetadataRegistry /> },
  admin: { comptes: <Admin />, codes: <AccessCodes />, sources: <KoboForms />, "kobo-mapping": <KoboMapping />, "sources-donnees": <Sources />, "guide-kobo": <KoboGuide />, parametres: <Settings /> },
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-right" theme="system" richColors />
          <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500 text-sm">Chargement…</div>}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/activer" element={<Activer />} />
              <Route path="/verify/:ref" element={<Verify />} />
              <Route path="/verifier/:ref" element={<Verify />} />
              <Route path="/app" element={<Protected><SyntheticBanner /><Layout /></Protected>}>
                <Route index element={<Home />} />
                {Object.entries(PAGES).map(([esp, pages]) => Object.entries(pages).map(([slug, el]) => (
                  <Route key={`${esp}/${slug}`} path={`${esp}/${slug}`} element={<Space espace={esp}>{el}</Space>} />
                )))}
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Route>
              <Route path="/" element={<Protected><Navigate to="/app" replace /></Protected>} />
              <Route path="*" element={<Protected><LegacyRedirect /></Protected>} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
